import { IErrorResponse } from '@common/api.models';
import { useProjectId } from '@config/config.hooks';
import { DISPLAY_DATE_TIME_FILENAME_FORMAT } from '@core/date.services';
import { DownloadFile } from '@file/DownloadFile';
import { IFileResponse, IDownloadDesignOrConstructionDrawingsRequest } from '@file/projectFile/projectFile.models';
import { downloadDesignOrConstructionDrawingsAsync, downloadFile } from '@file/projectFile/projectFile.services';
import {
  CircularProgressBar,
  Column,
  Icon,
  PaginatedTable,
  convertUtcStringToLocalTimezoneString,
  createTableCell,
  createTableColumn,
} from '@gcl/react-core';
import { useMutation } from '@tanstack/react-query';
import {
  IProjectTransmittalItemBase,
  IProjectTransmittalItemDrawing,
} from '@transmittal/transmittalItem/transmittalItem.models';
import { useFetchTransmittalItemsDrawings } from '@transmittal/transmittalItem/transmittalItem.queries';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useDocumentRegisterHistoryModal } from './DocumentRegisterHistoryModal.hooks';

interface IDocumentRegisterT1AccordionContentProps {
  masterScheduleId: number;
}
export const DocumentRegisterT1AccordionContent = ({ masterScheduleId }: IDocumentRegisterT1AccordionContentProps) => {
  const projectId = useProjectId();

  const [selectedTransmittalItem, setSelectedTransmittalItem] = useState<IProjectTransmittalItemBase>();
  const { renderModal: renderDocumentRegisterHistoryModal, openPortal: openDocumentRegisterHistoryModal } =
    useDocumentRegisterHistoryModal({
      projectMasterScheduleId: selectedTransmittalItem?.masterScheduleId,
      transmittalItemId: selectedTransmittalItem?.id,
    });

  const { data: drawingData } = useFetchTransmittalItemsDrawings({
    projectId,
    masterScheduleId,
  });

  const [downloadConstructionDrawingProgress, setDownloadConstructionDrawingProgress] = React.useState(0);
  const [downloadDesignDrawingProgress, setDownloadDesignDrawingProgress] = React.useState(0);
  const mutateDesignOrConsturctionDrawings = (isConstructionDrawing: boolean) => {
    return useMutation<IFileResponse, AxiosError<IErrorResponse>, IDownloadDesignOrConstructionDrawingsRequest, any>(
      downloadDesignOrConstructionDrawingsAsync,
      {
        onError: (error: AxiosError<IErrorResponse>) => {
          if (isConstructionDrawing) setDownloadConstructionDrawingProgress(0);
          else setDownloadDesignDrawingProgress(0);
        },
        onSuccess: (data, variables, context) => {
          if (isConstructionDrawing) setDownloadConstructionDrawingProgress(0);
          else setDownloadDesignDrawingProgress(0);
          downloadFile(data.file, data.filename);
        },
      },
    );
  };

  const {
    mutate: mutateDownloadConstructionDrawing,
    isLoading: isLoadingDownloadConstructionDrawing,
    error: errorDownloadConstructionDrawing,
    reset: resetDownloadConstructionDrawing,
  } = mutateDesignOrConsturctionDrawings(true);
  const {
    mutate: mutateDownloadDesignDrawing,
    isLoading: isLoadingDownloadDesignDrawing,
    error: errorDownloadDesignDrawing,
    reset: resetDownloadDesignDrawing,
  } = mutateDesignOrConsturctionDrawings(false);

  const downloadConstructionDrawing = (isZip: boolean) => {
    mutateDownloadConstructionDrawing({
      projectId,
      masterScheduleId,
      isZip,
      isConstructionDrawing: true,
      onDownloadProgressUpdate: (percentCompleted: number) => {
        setDownloadConstructionDrawingProgress(percentCompleted);
      },
    });
  };
  const downloadDesignDrawing = (isZip: boolean) => {
    mutateDownloadDesignDrawing({
      projectId,
      masterScheduleId,
      isZip,
      isConstructionDrawing: false,
      onDownloadProgressUpdate: (percentCompleted: number) => {
        setDownloadDesignDrawingProgress(percentCompleted);
      },
    });
  };

  const columns = React.useMemo(() => {
    return [
      createTableColumn({
        accessor: 'transmittalItem.docNo',
        Header: 'Drawing No.',

        Cell: createTableCell<string, any>(
          (value: any, item: IProjectTransmittalItemDrawing, index: number, cellExtras: { [key: string]: any }) => {
            return (
              <div
                onClick={(e) => {
                  setSelectedTransmittalItem(item.transmittalItem);
                  openDocumentRegisterHistoryModal(e);
                }}
                className="gcl-clickable"
              >
                {item.transmittalItem.docNo}
              </div>
            );
          },
        ),
      } as Column),
      // createTableColumn({
      //   accessor: 'drawingTitle',
      //   Header: 'Drawing Title',
      //   Cell: createTableCell<string, any>(
      //     (value: any, item: IProjectTransmittalItemDrawing, index: number, cellExtras: { [key: string]: any }) => {
      //       return <div>{item.transmittalItem.docNo}</div>;
      //     },
      //   ),
      // } as Column),
      createTableColumn({
        accessor: 'id1',
        Header: ({ cellExtras }: any) => {
          return (
            <>
              Design
              <Icon
                onClick={() => downloadDesignDrawing(true)}
                className="gcl-fl-lg gcl-clickable gcl-ml-xs"
                iconName="FileEarmarkZipFill"
              />
              <Icon
                onClick={() => downloadDesignDrawing(false)}
                className="gcl-fl-lg gcl-clickable"
                iconName="FilePdfFill"
              />
              {cellExtras.isLoadingDownloadDesignDrawing && (
                <CircularProgressBar
                  value={cellExtras.downloadDesignDrawingProgress}
                  className="gcl-gems-document-register-progress-bar"
                />
              )}
            </>
          );
        },
        Cell: createTableCell<string, any>(
          (value: any, item: IProjectTransmittalItemDrawing, index: number, cellExtras: { [key: string]: any }) => {
            if (!item.latestDesignDrawing?.revisionNo || !item.latestDesignDrawing.fileId) return null;
            return (
              <div className="gcl-gems-t1-detail-drawing-latest-drawing-col">
                <DownloadFile
                  text={item.latestDesignDrawing?.revisionNo}
                  attachmentId={item.latestDesignDrawing?.fileId}
                  projectId={projectId}
                  shouldNotChop
                />
                {item.latestDesignDrawing.dateOfIssue && (
                  <span className="gcl-ml-xs">
                    ({convertUtcStringToLocalTimezoneString(item.latestDesignDrawing.dateOfIssue)})
                  </span>
                )}
              </div>
            );
          },
        ),
      } as Column),
      createTableColumn({
        accessor: 'id2',
        Header: ({ cellExtras }: any) => {
          return (
            <>
              Construction
              <Icon
                onClick={() => downloadConstructionDrawing(true)}
                className="gcl-fl-lg gcl-clickable gcl-ml-xs"
                iconName="FileEarmarkZipFill"
              />
              <Icon
                onClick={() => downloadConstructionDrawing(false)}
                className="gcl-fl-lg gcl-clickable"
                iconName="FilePdfFill"
              />
              {cellExtras.isLoadingDownloadConstructionDrawing && (
                <CircularProgressBar
                  value={cellExtras.downloadConstructionDrawingProgress}
                  className="gcl-gems-document-register-progress-bar"
                />
              )}
            </>
          );
        },
        Cell: createTableCell<string, any>(
          (value: any, item: IProjectTransmittalItemDrawing, index: number, cellExtras: { [key: string]: any }) => {
            if (!item.latestConstructionDrawing?.revisionNo || !item.latestConstructionDrawing.fileId) return null;
            return (
              <div className="gcl-gems-t1-detail-drawing-latest-drawing-col">
                <DownloadFile
                  text={item.latestConstructionDrawing.revisionNo ?? item.latestConstructionDrawing.itemId.toString()}
                  attachmentId={item.latestConstructionDrawing.fileId}
                  projectId={projectId}
                />
                {item.latestConstructionDrawing.dateOfIssue && (
                  <span className="gcl-ml-xs">
                    {convertUtcStringToLocalTimezoneString(
                      item.latestConstructionDrawing.dateOfIssue,
                      DISPLAY_DATE_TIME_FILENAME_FORMAT,
                    )}
                  </span>
                )}
              </div>
            );
          },
        ),
      } as Column),
    ];
  }, []);

  return (
    <>
      <ApiErrorDisplay
        errors={[
          {
            error: errorDownloadConstructionDrawing,
            genericError: 'Failed to download file',
            reset: resetDownloadConstructionDrawing,
          },
          {
            error: errorDownloadDesignDrawing,
            genericError: 'Failed to download file',
            reset: resetDownloadDesignDrawing,
          },
        ]}
      />
      <PaginatedTable
        allowSelectRow={false}
        disablePagination
        items={drawingData ?? []}
        columns={columns}
        cellExtras={{
          isLoadingDownloadDesignDrawing,
          isLoadingDownloadConstructionDrawing,
          downloadConstructionDrawingProgress,
          downloadDesignDrawingProgress,
        }}
      />
      {renderDocumentRegisterHistoryModal()}
    </>
  );
};
